import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Select, Tag, message, Upload, 
  Card, Row, Col, Checkbox, Divider, Spin, Space, Typography,
  Avatar, TimePicker, Collapse, Tabs
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  UploadOutlined, UserOutlined, DownOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DoctorManagement = () => {
  const [doctors, setDoctors] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [newSpecName, setNewSpecName] = useState('');
  const [showAddSpec, setShowAddSpec] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    genders: ['Male', 'Female', 'Other'],
    specialties: []
  });
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState({
    name: '', gender: '', specialty: ''
  });
  const [imageUrl, setImageUrl] = useState('');
  const [activeTab, setActiveTab] = useState('1');
  const [timeFormat, setTimeFormat] = useState('HH:mm');

  const apiUrl = import.meta.env.VITE_API_URL;

  useEffect(() => {
    fetchFilterOptions();
    fetchDoctors();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${apiUrl}/doctor-filter-options/`);
      setFilterOptions(prev => ({
        ...prev,
        specialties: response.data.specialties || []
      }));
    } catch (error) {
      message.error('Failed to fetch filter options');
    }
  };

  const fetchDoctors = async (params = {}) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/doctors/`, { params });
      setDoctors(response.data || []);
    } catch (error) {
      message.error('Failed to fetch doctors');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecializations = async () => {
    try {
      const response = await axios.get(`${apiUrl}/specializations/`);
      setSpecializations(response.data || []);
    } catch (error) {
      message.error('Failed to fetch specializations');
    }
  };

  const uploadImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await axios.post(`${apiUrl}/images/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImageUrl(response.data.image);
      return response.data.image;
    } catch (error) {
      message.error('Image upload failed');
      throw error;
    }
  };

  const showModal = (doctor = null) => {
    setCurrentDoctor(doctor);
    setImageUrl('');
    
    if (doctor) {
      // Format working hours with dayjs objects
      const formattedWorkingHours = {};
      DAYS_OF_WEEK.forEach(day => {
        const hours = doctor.working_hours?.[day] || {};
        formattedWorkingHours[day] = {
          start: hours.start ? dayjs(hours.start, 'HH:mm') : null,
          end: hours.end ? dayjs(hours.end, 'HH:mm') : null
        };
      });

      form.setFieldsValue({
        ...doctor,
        specialty_ids: doctor.specialty?.map(s => s.id),
        availability: doctor.availability || [],
        working_hours: formattedWorkingHours,
        image_url: doctor.image_url
      });
      
      if (doctor.image_url) setImageUrl(doctor.image_url);
    } else {
      form.resetFields();
      form.setFieldsValue({ availability: [] });
    }
    setVisible(true);
    setActiveTab('1');
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // Prepare working hours data
      const workingHoursData = {};
      if (values.availability) {
        values.availability.forEach(day => {
          const hours = values.working_hours?.[day] || {};
          workingHoursData[day] = {
            start: hours.start ? hours.start.format('HH:mm') : '09:00',
            end: hours.end ? hours.end.format('HH:mm') : '17:00'
          };
        });
      }

      const doctorData = {
        ...values,
        image_url: imageUrl,
        specialty_ids: values.specialty_ids,
        availability: values.availability,
        working_hours: workingHoursData
      };

      if (currentDoctor) {
        await axios.put(`${apiUrl}/doctors/${currentDoctor.id}/`, doctorData);
        message.success('Doctor updated successfully');
      } else {
        await axios.post(`${apiUrl}/doctors/`, doctorData);
        message.success('Doctor added successfully');
      }
      
      fetchDoctors();
      setVisible(false);
    } catch (error) {
      message.error('Operation failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = (day, field, time) => {
    const currentHours = form.getFieldValue('working_hours') || {};
    const updatedHours = {
      ...currentHours,
      [day]: {
        ...currentHours[day],
        [field]: time
      }
    };
    form.setFieldsValue({ working_hours: updatedHours });
  };

  const handleAvailabilityChange = (checkedValues) => {
    const currentHours = form.getFieldValue('working_hours') || {};
    const updatedHours = { ...currentHours };
    
    // Initialize default times for newly selected days
    checkedValues.forEach(day => {
      if (!updatedHours[day] || !updatedHours[day].start) {
        updatedHours[day] = {
          start: dayjs('09:00', 'HH:mm'),
          end: dayjs('17:00', 'HH:mm')
        };
      }
    });
    
    form.setFieldsValue({ working_hours: updatedHours });
  };

  const setAllTimes = (startTime, endTime) => {
    const availability = form.getFieldValue('availability') || [];
    const currentHours = form.getFieldValue('working_hours') || {};
    const updatedHours = { ...currentHours };
    
    availability.forEach(day => {
      updatedHours[day] = {
        start: startTime ? dayjs(startTime, 'HH:mm') : null,
        end: endTime ? dayjs(endTime, 'HH:mm') : null
      };
    });
    
    form.setFieldsValue({ working_hours: updatedHours });
  };

  const columns = [
    {
      title: 'Doctor',
      dataIndex: 'name',
      render: (text, record) => (
        <Space>
          <Avatar src={record.image_url} icon={<UserOutlined />} size="large" />
          <div>
            <Text strong>{text}</Text><br/>
            <Text type="secondary">{record.qualification}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Specialty',
      dataIndex: 'specialty',
      render: (specialties) => (
        <Space size="small" wrap>
          {specialties.map(s => <Tag key={s.id} color="blue">{s.name}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Availability',
      dataIndex: 'availability',
      render: (days, record) => (
        <div style={{ maxWidth: 250 }}>
          {days?.length > 0 ? (
            <Collapse ghost size="small">
              <Panel header={`${days.length} day(s)`} key="availability">
                <Space direction="vertical" size="small">
                  {days.map(day => (
                    <div key={day} className="flex justify-between">
                      <Text strong>{day}:</Text>
                      <Text>
                        {record.working_hours?.[day]?.start || '--:--'} - {record.working_hours?.[day]?.end || '--:--'}
                      </Text>
                    </div>
                  ))}
                </Space>
              </Panel>
            </Collapse>
          ) : <Text type="secondary">Not set</Text>}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)} />
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Card 
        title="Doctor Management"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Add Doctor
          </Button>
        }
      >
        <Row gutter={16} className="mb-6">
          <Col span={8}>
            <Input
              placeholder="Search doctors..."
              prefix={<SearchOutlined />}
              value={searchParams.name}
              onChange={(e) => setSearchParams({...searchParams, name: e.target.value})}
            />
          </Col>
          <Col span={8}>
            <Select
              className="w-full"
              placeholder="Filter by gender"
              value={searchParams.gender}
              onChange={(value) => setSearchParams({...searchParams, gender: value})}
            >
              {filterOptions.genders.map(gender => (
                <Option key={gender} value={gender}>{gender}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Space>
              <Button type="primary" onClick={() => fetchDoctors(searchParams)}>
                Search
              </Button>
              <Button onClick={() => {
                setSearchParams({ name: '', gender: '', specialty: '' });
                fetchDoctors();
              }}>
                Reset
              </Button>
            </Space>
          </Col>
        </Row>

        <Table 
          columns={columns} 
          dataSource={doctors} 
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={currentDoctor ? 'Edit Doctor' : 'Add New Doctor'}
        open={visible}
        onOk={handleSubmit}
        onCancel={() => setVisible(false)}
        width={800}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Basic Info" key="1">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
                    <Input placeholder="Dr. John Smith" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
                    <Select placeholder="Select gender">
                      {filterOptions.genders.map(gender => (
                        <Option key={gender} value={gender}>{gender}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="qualification" label="Qualifications" rules={[{ required: true }]}>
                    <Input placeholder="MD, Cardiology" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="experience" label="Experience (years)" rules={[{ required: true }]}>
                    <Input type="number" min="0" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="image_url" label="Profile Image">
                <Upload
                  listType="picture-card"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) message.error('You can only upload images!');
                    return isImage;
                  }}
                  customRequest={async ({ file, onSuccess }) => {
                    try {
                      const url = await uploadImage(file);
                      form.setFieldsValue({ image_url: url });
                      onSuccess();
                    } catch (error) {
                      message.error('Upload failed');
                    }
                  }}
                >
                  {imageUrl ? (
                    <Avatar src={imageUrl} size={100} />
                  ) : (
                    <div>
                      <UploadOutlined />
                      <div>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </TabPane>

            <TabPane tab="Specializations" key="2">
              <Form.Item
                name="specialty_ids"
                label="Specializations"
                rules={[{ required: true, message: 'Please select at least one' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select specializations"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <div style={{ padding: '0 8px 4px' }}>
                        {showAddSpec ? (
                          <Space.Compact style={{ width: '100%' }}>
                            <Input
                              value={newSpecName}
                              onChange={(e) => setNewSpecName(e.target.value)}
                              placeholder="New specialization"
                            />
                            <Button type="primary" onClick={addNewSpecialization}>
                              Add
                            </Button>
                          </Space.Compact>
                        ) : (
                          <Button 
                            type="text" 
                            icon={<PlusOutlined />} 
                            onClick={() => setShowAddSpec(true)}
                          >
                            Add new specialization
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                >
                  {specializations.map(spec => (
                    <Option key={spec.id} value={spec.id}>{spec.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </TabPane>

            <TabPane tab="Availability" key="3">
              <Form.Item
                name="availability"
                label="Available Days"
                rules={[{ required: true, message: 'Please select at least one day' }]}
              >
                <Checkbox.Group 
                  className="w-full"
                  onChange={handleAvailabilityChange}
                >
                  <Row gutter={[16, 8]}>
                    {DAYS_OF_WEEK.map(day => (
                      <Col span={8} key={day}>
                        <Checkbox value={day}>{day}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>

              <div className="mb-4">
                <Text strong>Quick Set All Times:</Text>
                <Space className="ml-4">
                  <TimePicker
                    format="HH:mm"
                    placeholder="Start time"
                    onChange={(time) => setAllTimes(time?.format('HH:mm'), null)}
                  />
                  <Text>-</Text>
                  <TimePicker
                    format="HH:mm"
                    placeholder="End time"
                    onChange={(time) => setAllTimes(null, time?.format('HH:mm'))}
                  />
                  <Button onClick={() => setAllTimes('09:00', '17:00')}>
                    Set 9AM-5PM
                  </Button>
                </Space>
              </div>

              <div className="space-y-4">
                {DAYS_OF_WEEK.map(day => {
                  const availability = form.getFieldValue('availability') || [];
                  if (!availability.includes(day)) return null;
                  
                  const dayHours = form.getFieldValue(['working_hours', day]) || { start: null, end: null };
                  
                  return (
                    <Card key={day} size="small" title={day}>
                      <Row gutter={16} align="middle">
                        <Col span={10}>
                          <Form.Item
                            name={['working_hours', day, 'start']}
                            label="Start Time"
                            rules={[{ required: true }]}
                          >
                            <TimePicker 
                              format="HH:mm"
                              minuteStep={15}
                              className="w-full"
                              value={dayHours.start}
                              onChange={(time) => handleTimeChange(day, 'start', time)}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={4} className="text-center">
                          <Text>-</Text>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            name={['working_hours', day, 'end']}
                            label="End Time"
                            rules={[{ required: true }]}
                          >
                            <TimePicker 
                              format="HH:mm"
                              minuteStep={15}
                              className="w-full"
                              value={dayHours.end}
                              onChange={(time) => handleTimeChange(day, 'end', time)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </div>
            </TabPane>

            <TabPane tab="About" key="4">
              <Form.Item name="about" label="About Doctor">
                <TextArea rows={4} />
              </Form.Item>
            </TabPane>
          </Tabs>
        </Form>
      </Modal>
    </div>
  );
};

export default DoctorManagement;